/**
 * Monopage d'entrée — la seule page rendue hors `AppShell` (§ App.jsx).
 *
 * Gabarit fermé du studio (exiUI, coquille.md §6.5 et mises-en-page.md §0bis) : la même page
 * dans les sept produits. Deux rails, une bande titre, une bande hachurée, la présentation du
 * produit, la carte « Connexion », une bande pied. Tout le texte de présentation vient de
 * `@exidis/ui/catalogue` — rien n'est écrit ici, la vitrine exidis.fr lit le même objet.
 *
 * Un seul bouton. En mode CIAM (`ciamConfigured`), il lance `startLogin()` (`lib/connexion.js`
 * → MSAL `loginRedirect`, lot 2 de la spec CIAM) : l'adresse, le code à usage unique et le
 * choix du fournisseur social (Google fédéré, D8 bis) se font entièrement sur la page hébergée
 * par le tenant Exidis (user flow `signin`), et le bouton ignore `providers`. Hors
 * configuration CIAM — production avant sa recette, `npm run dev:auth` sans `VITE_CIAM_*` côté
 * client — repli sur le flux OAuth serveur historique (`startLegacyLogin`, D7), comme avant
 * ce lot : le fournisseur vient de `fournisseurPrincipal(providers)`.
 */

import { useTranslation } from 'react-i18next';
import { produit } from '@exidis/ui/catalogue';

import { useSession } from '../contexts/SessionContext.jsx';
import { ciamConfigured } from '../lib/auth.js';
import MenuLangue from '../components/common/MenuLangue.jsx';
import { fournisseurPrincipal, startLegacyLogin, startLogin } from '../lib/connexion.js';
import { BRAND, BRAND_GLYPH, BTN_DARK, EYEBROW } from '../ui/theme.js';
import GlypheExiPlan from '../components/common/GlypheExiPlan.jsx';

/**
 * Code d'échec porté par `authError` dans l'URL — hérité du callback OAuth serveur, tant que
 * la coexistence dure (D7).
 */
const AUTH_ERROR_KEYS = {
  login_failed: 'login.errorLoginFailed',
  server_error: 'login.errorServer',
};

const SITE_EXIDIS = 'https://exidis.fr';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { providers, authError: sessionAuthError } = useSession();
  const principal = fournisseurPrincipal(providers);

  const langue = i18n.resolvedLanguage || i18n.language?.split('-')[0] || 'fr';
  const fiche = produit('exiplan', langue);

  // Côté porteur (CIAM), une erreur vient du rejet de `handleRedirectPromise()` — posée par
  // `SessionContext` (`initAuth()`), jamais dans l'URL : aucun détail technique ici non plus,
  // même clé i18n que le repli legacy (§9.2 de la revue).
  const urlAuthError = new URLSearchParams(window.location.search).get('authError');
  const errorKey = sessionAuthError
    ? AUTH_ERROR_KEYS.login_failed
    : urlAuthError && AUTH_ERROR_KEYS[urlAuthError];

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <a className="skip-link" href="#connexion">
        {t('a11y.skipToContent')}
      </a>

      <div className="wrap-etroit relative flex w-full flex-1 flex-col">
        {/* bande titre : studio · produit, langue — les deux liens vers la vitrine */}
        <header className="flex items-center justify-between gap-4 border-b-2 border-line bg-surface px-7 py-4">
          <span className="font-mono text-[11.5px] uppercase tracking-eyebrow text-ink-faint">
            <a href={SITE_EXIDIS} className="hover:text-ink">
              Exidis
            </a>{' '}
            <span className="text-ink-soft">
              ·{' '}
              <a href={fiche.siteUrl} className="hover:text-ink">
                {fiche.name}
              </a>
            </span>
          </span>
          <MenuLangue />
        </header>

        {/* bande hachurée : un vide, jamais sous du contenu */}
        <div className="hatch h-16 border-b-2 border-line" aria-hidden="true" />

        <main className="flex flex-1 flex-col items-stretch px-6 py-11" id="connexion">
          {/* présentation — produit(slug, lang), et rien d'autre */}
          <section className="mx-auto mb-9 w-full max-w-[560px]" aria-labelledby="titre-produit">
            <h1 className={`${BRAND} !text-lg`} id="titre-produit">
              <span className={BRAND_GLYPH} aria-hidden="true">
                <GlypheExiPlan />
              </span>
              {fiche.name}
            </h1>
            <p className="mt-4 text-[1.375rem] font-semibold leading-tight text-ink">
              {fiche.tagline}
            </p>
            <p className="mt-3.5 max-w-[60ch] text-[15px] text-ink-soft">{fiche.description}</p>
            <ul className="mt-5 border-t-2 border-line">
              {fiche.points.map((point) => (
                <li
                  key={point}
                  className="grid grid-cols-[22px_1fr] items-baseline gap-x-2.5 border-b-2 border-line py-2.5 text-sm text-ink-soft"
                >
                  <span className="h-2 w-2 self-center bg-accent-trait" aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
          </section>

          {/* p-3 « passe-partout » : sans lui, les équerres (inset:-2px) sont rognées. */}
          <div className="mx-auto w-[420px] max-w-full p-3">
            <section
              className="relative border-2 border-line bg-surface-2 px-8 py-9 shadow-plank"
              aria-labelledby="titre-connexion"
            >
              <span className="ticks" aria-hidden="true" />

              <p className={EYEBROW} id="titre-connexion">
                {t('login.eyebrow')}
              </p>

              {errorKey && (
                <p role="alert" className="mt-3 border-2 border-line bg-paper p-3 text-sm text-ink">
                  {t(errorKey)}
                </p>
              )}

              <p className="mt-3 text-[13px] text-ink-soft">{t('login.aide')}</p>

              {ciamConfigured || principal ? (
                <button
                  type="button"
                  className={`${BTN_DARK} mt-4 w-full`}
                  onClick={() => (ciamConfigured ? startLogin() : startLegacyLogin(principal))}
                >
                  {t('login.action')}
                </button>
              ) : (
                <p className="mt-4 text-sm text-ink-soft">
                  {t('login.noProvidersPrefix')}
                  <code className="font-mono text-ink">AUTH_BYPASS=1</code>
                  {t('login.noProvidersSuffix')}
                </p>
              )}

              <p className="mt-2.5 text-[11px] text-ink-faint">{t('login.mention')}</p>
            </section>
          </div>
        </main>

        {/* bande pied : copyright, retour vers la vitrine, liens légaux */}
        <footer className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t-2 border-line bg-surface px-7 py-3 font-mono text-[10.5px] uppercase tracking-label text-ink-faint">
          <span>© {new Date().getFullYear()} Exidis</span>
          <nav className="flex gap-4" aria-label={t('login.liensPied')}>
            <a
              href={fiche.siteUrl}
              className="border-b-2 border-line hover:border-ink-faint hover:text-ink-soft"
            >
              {t('login.enSavoirPlus')}
            </a>
          </nav>
        </footer>
      </div>
    </div>
  );
}
